import React, { MouseEventHandler, ReactNode } from 'react';
import {
  NavLink,
  NavLinkProps,
  NavLinkRenderProps,
  useLocation,
} from 'react-router-dom';
import { FunnelIcon } from '@heroicons/react/24/outline';
import {
  sendAnalyticsEvent,
  Tooltip,
  useAccessControl,
} from '@plerionhq/design-system';
import { ProtectedComponent } from '../shared/ProtectedComponent/ProtectedComponent';
import { SideNavItem, SideNavMode } from './SideNav';
import { SIDE_NAME_ICON_CONFIG } from './config';
import './SideNavElement.css';
import { useTranslation } from 'react-i18next';
import { navItemLabel } from 'api/nav-items';

const DivElement = ({
  children,
  className,
  onClick,
}: {
  children: React.ReactNode | Iterable<React.ReactNode>;
  className?: string | ((props: NavLinkRenderProps) => string | undefined);
  onClick?: MouseEventHandler | undefined;
}) => {
  const divClassName =
    typeof className === 'string'
      ? className
      : className?.({
          isActive: false,
          isPending: false,
          isTransitioning: false,
        });
  return (
    <div
      onClick={onClick as MouseEventHandler<HTMLDivElement> | undefined}
      className={divClassName}
    >
      {children}
    </div>
  );
};

// An item can carry RBAC actions, a legacy permission list, or both, and
// either passing is enough: a custom role holds actions and no legacy
// permissions, a built-in role holds both.
const NavAccessControl = ({
  item,
  children,
}: {
  item: SideNavItem;
  children: ReactNode;
}) => {
  const { can, isLoading } = useAccessControl();
  const actions = item.action ? [item.action].flat() : [];
  if (actions.length === 0 && !item.permissions) {
    return <>{children}</>;
  }
  // `can` is fail-closed while entitlements load, so an item holding both
  // gates falls through to the legacy check rather than flickering.
  if (!isLoading && actions.some((action) => can({ action }))) {
    return <>{children}</>;
  }
  if (!item.permissions) {
    return <></>;
  }
  return (
    <ProtectedComponent permission={item.permissions}>
      {children}
    </ProtectedComponent>
  );
};

export const SideNavElement = ({
  item,
  mode,
  isExpanded,
  rightIcon,
  activeSubMenu,
  setActiveSubMenu,
  setAllowHoverSubMenu,
  onClick,
  linkRef,
  ...otherProps
}: {
  item: SideNavItem;
  mode?: SideNavMode;
  isExpanded?: boolean;
  activeSubMenu?: string;
  rightIcon?: ReactNode;
  setActiveSubMenu?: (submenu?: string) => void;
  setAllowHoverSubMenu?: (submenu?: string) => void;
  linkRef?: React.ForwardedRef<HTMLAnchorElement>;
  onClick?: () => void;
} & Omit<NavLinkProps, 'to' | 'onClick'>) => {
  const { t } = useTranslation('common');
  const label = navItemLabel(t, item.name);
  const Icon = SIDE_NAME_ICON_CONFIG[item.icon] || FunnelIcon;
  const isBeta = item.entitlement?.includes('beta');
  const isAlpha = item.entitlement?.includes('alpha');
  const isNew = item.isNew;
  const Element = item.url ? NavLink : DivElement;
  const hasSubMenu = !!item?.subItems && item?.subItems?.length > 0;
  const hasActiveSubMenu = activeSubMenu === item.key;
  const [showTooltip, setShowTooltip] = React.useState(false);
  const location = useLocation();
  const pathName = location.pathname;

  const activeClass = 'bg-br-primary/10 !text-br-primary group active';
  const isCustomReportPreviewPage =
    pathName.includes('/settings/custom-reports') &&
    window.location.search.includes('?mode=readOnly');
  const isSettingsPage =
    pathName === '/settings' &&
    !window.location.search.includes('?mode=readOnly');

  // The top-level Settings item shares its key with its Overview sub-item, so
  // it is told apart by its sub-menu. It stays lit on every page in that
  // menu, except a pinned dashboard, which lights its own item.
  const isSettingsSection =
    item.key === 'settings' &&
    hasSubMenu &&
    !isCustomReportPreviewPage &&
    !!item.subItems?.some(
      (sub) =>
        !!sub.url &&
        (pathName === sub.url || pathName.startsWith(`${sub.url}/`)),
    );

  const isPinnedCustomReportNavElement =
    item.url?.includes('/settings/custom-reports') &&
    item.url.includes('?mode=readOnly');

  const getActiveClass = () => {
    switch (item.key) {
      case 'pinned-custom-reports':
        return isCustomReportPreviewPage ? activeClass : '';
      case 'settings':
        return isSettingsPage || isSettingsSection ? activeClass : '';
      default:
        return activeClass;
    }
  };

  const handleDisplayTooltip = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    if (
      isPinnedCustomReportNavElement &&
      event.currentTarget &&
      event.currentTarget.offsetWidth < event.currentTarget.scrollWidth
    ) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  };

  const getTooltipText = () => {
    if (isExpanded) {
      if (showTooltip) {
        return label;
      }
      return item.toolTipText || '';
    } else {
      return item.toolTipText || label;
    }
  };

  const baseLinkClassName =
    'relative flex w-full items-center gap-2 truncate whitespace-nowrap  rounded-md p-2 text-sm font-medium text-br-primary dark:text-br-secondary hover:bg-br-primary/10';

  // NavAccessControl is outermost so a denied item renders nothing at all;
  // gating only the inner link leaves empty wrappers, which show as gaps in
  // the nav for users missing the permission.
  return (
    <NavAccessControl item={item}>
      <Tooltip
        label={getTooltipText()}
        placement="left"
        strategy="fixed"
        className="cursor-pointer"
      >
        <div className="relative w-full cursor-pointer">
          <Element
            onMouseOver={handleDisplayTooltip}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={(e) => {
              sendAnalyticsEvent('click navigation menu', {
                event_category: 'navigation',
                event_label: `${item.name}`,
              });
              if (hasSubMenu) {
                setActiveSubMenu?.(item.key);
                setAllowHoverSubMenu?.(item.key);
              } else {
                // Clear the hover submenu even when the click does not change
                // the pathname (e.g. re-clicking the current page's item);
                // otherwise it reopens on every sidebar hover.
                setActiveSubMenu?.(undefined);
                setAllowHoverSubMenu?.(undefined);
              }
              onClick?.();
              if (hasSubMenu) {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
            draggable={false}
            id={item.name.toLowerCase().replace(/ /g, '-')}
            className={({ isActive }) =>
              (isActive || hasActiveSubMenu || isSettingsSection
                ? getActiveClass() + ' !bg-br-primary/20'
                : ' ') + baseLinkClassName
            }
            ref={linkRef}
            state={{ resetToDefaults: true }}
            {...otherProps}
            to={item.url || ''}
          >
            <Icon className={`h-6 w-6 flex-shrink-0`} strokeWidth="1.5" />
            {(mode === 'expanded' || isExpanded) && <span>{label}</span>}
            {isExpanded && <div className="mr-2 ml-auto">{rightIcon}</div>}
            {isAlpha && (mode === 'expanded' || isExpanded) && (
              <NavigationTag tag="Alpha" />
            )}
            {isBeta && (mode === 'expanded' || isExpanded) && (
              <NavigationTag tag="Beta" />
            )}
          </Element>
          {isNew &&
            (mode === 'expanded' || isExpanded ? (
              <NavigationTag
                tag="New"
                className="z-10 border-red-600 bg-red-600"
              />
            ) : (
              <span className="new-nav-item-tag absolute top-0.5 right-0.5 z-10 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600"></span>
              </span>
            ))}
        </div>
      </Tooltip>
    </NavAccessControl>
  );
};

const NavigationTag = ({
  tag,
  className = '',
}: {
  tag: string;
  className?: string;
}) => (
  <div
    className={
      'border-br-primary bg-br-primary absolute -top-1 right-0 rounded-md border px-1 py-0.5 text-xs leading-none text-white ' +
      className
    }
  >
    {tag}
  </div>
);
